import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Camera,
  ChevronLeft,
  ClipboardPaste,
  Image as ImageIcon,
  ImagePlus,
  Loader2,
  Minus,
  Plus,
  Scan,
  Star,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { createWishItem } from "@/services/wishlists";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wishlistId: string;
  onAdded: () => void;
}

type ExtractedMetadata = {
  blocked?: boolean;
  currency?: string | null;
  description?: string | null;
  image?: string | null;
  price?: number | null;
  source?: "direct" | "reader" | "none";
  title?: string | null;
  url?: string;
};

const USELESS_TITLES = [
  "503 - Service Unavailable Error",
  "Page Not Found",
  "Access Denied",
  "Robot Check",
  "Captcha",
];

const AddWishModal = ({ open, onOpenChange, wishlistId, onAdded }: Props) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const linkInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<"link" | "photo-picker" | "uploading" | "manual">("link");
  const [linkUrl, setLinkUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [category, setCategory] = useState("");
  const [isMostDesired, setIsMostDesired] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const reset = () => {
    setMode("link");
    setLinkUrl("");
    setTitle("");
    setDescription("");
    setPrice("");
    setQuantity(1);
    setCategory("");
    setIsMostDesired(false);
    setImageUrl("");
    setImageFile(null);
    setExtracting(false);
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const isUsefulTitle = (value?: string | null) => {
    if (!value) return false;
    return !USELESS_TITLES.some((t) => t.toLowerCase() === value.trim().toLowerCase());
  };

  const applyMetadata = (data: ExtractedMetadata) => {
    let applied = false;
    if (isUsefulTitle(data.title)) { setTitle(data.title!.trim()); applied = true; }
    if (data.description) { setDescription(data.description.trim()); applied = true; }
    if (data.image) { setImageUrl(data.image); applied = true; }
    if (typeof data.price === "number" && Number.isFinite(data.price) && data.price > 0) {
      setPrice(String(data.price)); applied = true;
    }
    if (data.url) setLinkUrl(data.url);
    return applied;
  };

  const extractMetadata = async (
    rawUrl: string,
    options?: { switchToManual?: boolean; showFailureToast?: boolean }
  ) => {
    const switchToManual = options?.switchToManual ?? true;
    const showFailureToast = options?.showFailureToast ?? false;
    const trimmedUrl = rawUrl.trim();
    if (!trimmedUrl || extracting) return false;

    setExtracting(true);
    try {
      const { data, error } = await supabase.functions.invoke<ExtractedMetadata>(
        "extract-url-metadata",
        { body: { url: trimmedUrl } }
      );
      const extracted = !error && data ? applyMetadata(data) : false;
      if (switchToManual) setMode("manual");
      if (!extracted && showFailureToast) {
        toast({ title: "Couldn't fetch item details", description: "You can add the item details manually." });
      }
      return extracted;
    } catch {
      if (switchToManual) setMode("manual");
      if (showFailureToast) {
        toast({ title: "Couldn't fetch item details", description: "You can still finish adding the item manually." });
      }
      return false;
    } finally {
      setExtracting(false);
    }
  };

  const handleLinkPaste = (value: string) => {
    setLinkUrl(value);
    window.setTimeout(() => {
      void extractMetadata(value, { switchToManual: true, showFailureToast: true });
    }, 0);
  };

  const handleClipboardPaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text?.trim()) handleLinkPaste(text.trim());
    } catch {
      linkInputRef.current?.focus();
    }
  };

  const handleContinueWithLink = async () => {
    if (!linkUrl.trim()) {
      toast({ title: "Enter a link or add manually" });
      return;
    }
    await extractMetadata(linkUrl, { switchToManual: true, showFailureToast: true });
  };

  const handlePhotoSelected = (file: File, form?: HTMLFormElement | null) => {
    if (!form) {
      toast({ title: "Couldn't start image search", description: "Try again or add the item manually." });
      setMode("manual");
      return;
    }

    setImageFile(file);
    setMode("uploading");

    try {
      form.submit();
      window.setTimeout(() => {
        toast({ title: "Image search opened", description: "Choose a result, then come back and paste the product link." });
        setMode("link");
        form.reset();
      }, 150);
    } catch {
      toast({ title: "Couldn't start image search", description: "Try again or add the item manually." });
      setMode("manual");
    }
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);

    let finalImageUrl = imageUrl;
    if (imageFile && !imageUrl) {
      const ext = imageFile.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `wish-items/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("message-attachments").upload(path, imageFile);
      if (!upErr) {
        const { data: urlData } = supabase.storage.from("message-attachments").getPublicUrl(path);
        finalImageUrl = urlData.publicUrl;
      }
    }

    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await createWishItem({
      wishlist_id: wishlistId,
      title: title.trim(),
      description: description.trim() || undefined,
      price: price ? parseFloat(price) : undefined,
      quantity,
      link_url: linkUrl.trim() || undefined,
      image_url: finalImageUrl || undefined,
      category: category.trim() || undefined,
      is_most_desired: isMostDesired,
      added_by_user_id: user?.id,
    });

    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not add wish." });
    } else {
      toast({ title: "Added!", description: `${title} added to your list.` });
      reset();
      onAdded();
    }
    setSaving(false);
  };

  const hiddenInputs = (
    <>
      <form
        action="https://www.google.com/searchbyimage/upload"
        method="post"
        encType="multipart/form-data"
        target="_blank"
        className="hidden"
      >
        <input type="hidden" name="hl" value="en" />
        <input
          ref={cameraInputRef}
          type="file"
          name="encoded_image"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handlePhotoSelected(f, e.currentTarget.form);
          }}
        />
      </form>
      <form
        action="https://www.google.com/searchbyimage/upload"
        method="post"
        encType="multipart/form-data"
        target="_blank"
        className="hidden"
      >
        <input type="hidden" name="hl" value="en" />
        <input
          ref={galleryInputRef}
          type="file"
          name="encoded_image"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handlePhotoSelected(f, e.currentTarget.form);
          }}
        />
      </form>
    </>
  );

  const headerTitle = mode === "link" || mode === "uploading"
    ? t("wishlist.addWish", { defaultValue: "Add Wish" })
    : mode === "photo-picker"
      ? "Search by Photo"
      : "Add Details";

  const showBack = mode === "manual" || mode === "photo-picker";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl md:max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl relative"
          >
            {hiddenInputs}

            {/* Header */}
            <div className="flex items-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10 px-4">
              {showBack ? (
                <button
                  onClick={() => setMode("link")}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
              ) : (
                <div className="w-8" />
              )}
              <h2 className="text-lg font-bold text-foreground font-serif flex-1 text-center">
                {headerTitle}
              </h2>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              {/* ─── LINK VIEW ─── */}
              {mode === "link" && (
                <div className="flex flex-col items-center py-8">
                  <p className="text-sm text-muted-foreground mb-6">
                    {t("wishlist.pasteLink", { defaultValue: "Paste a link from anywhere" })}
                  </p>

                  <div className="w-full flex items-center gap-2">
                    <Input
                      ref={linkInputRef}
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData("text");
                        if (pasted.trim()) handleLinkPaste(pasted.trim());
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter" && linkUrl.trim()) handleContinueWithLink(); }}
                      placeholder="https://"
                      className="flex-1 border-border"
                      disabled={extracting}
                    />
                    {extracting ? (
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <Loader2 size={18} className="animate-spin text-primary" />
                      </div>
                    ) : (
                      <>
                        <button onClick={handleClipboardPaste} className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0 text-muted-foreground hover:text-foreground transition-colors" title="Paste from clipboard">
                          <ClipboardPaste size={18} />
                        </button>
                        <button onClick={() => { galleryInputRef.current?.click(); }} className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0 text-muted-foreground hover:text-foreground transition-colors" title="Search by photo">
                          <Scan size={18} />
                        </button>
                      </>
                    )}
                  </div>

                  <p className="text-center text-sm text-muted-foreground mt-8">
                    {t("wishlist.noLink", { defaultValue: "Don't have a link?" })}{" "}
                    <button onClick={() => setMode("manual")} className="font-semibold text-foreground underline underline-offset-2">
                      {t("wishlist.addManually", { defaultValue: "Add Manually" })}
                    </button>
                  </p>
                </div>
              )}

              {/* ─── PHOTO PICKER ─── */}
              {mode === "photo-picker" && (
                <div className="py-4">
                  <p className="text-sm text-muted-foreground text-center mb-6">
                    Take a photo or choose from your library to search for similar products on Google
                  </p>

                  <div className="space-y-3">
                    <button onClick={() => cameraInputRef.current?.click()} className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all text-left">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Camera size={20} className="text-primary" />
                      </div>
                      <span className="font-medium text-foreground">Take Photo</span>
                    </button>

                    <button onClick={() => { galleryInputRef.current?.click(); }} className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all text-left">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <ImagePlus size={20} className="text-primary" />
                      </div>
                      <span className="font-medium text-foreground">Choose from Library</span>
                    </button>
                  </div>
                </div>
              )}

              {/* ─── UPLOADING ─── */}
              {mode === "uploading" && (
                <div className="flex flex-col items-center py-12">
                  {imageFile && (
                    <div className="w-32 aspect-square rounded-2xl overflow-hidden bg-muted mb-5">
                      <img src={URL.createObjectURL(imageFile)} alt="Uploading" className="h-full w-full object-cover" />
                    </div>
                  )}
                  <Loader2 size={24} className="animate-spin text-primary mb-3" />
                  <p className="text-sm text-muted-foreground">Opening Google Lens…</p>
                </div>
              )}

              {/* ─── MANUAL FORM ─── */}
              {mode === "manual" && (
                <div className="space-y-4">
                  {/* Most desired toggle */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => setIsMostDesired(!isMostDesired)}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                        isMostDesired ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
                      )}
                    >
                      <Star size={13} fill={isMostDesired ? "currentColor" : "none"} />
                      Most Desired
                    </button>
                  </div>

                  {/* Desktop: side-by-side image + fields / Mobile: stacked */}
                  <div className="flex flex-col md:flex-row gap-5">
                    {/* Image */}
                    <label className="block cursor-pointer shrink-0">
                      {imageUrl || imageFile ? (
                        <div className="relative mx-auto md:mx-0 w-40 md:w-48 aspect-square overflow-hidden rounded-xl border-2 border-primary/30 bg-muted">
                          <img src={imageFile ? URL.createObjectURL(imageFile) : imageUrl} alt="Wish" className="h-full w-full object-cover" />
                        </div>
                      ) : (
                        <div className="mx-auto md:mx-0 flex w-40 md:w-48 aspect-square items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/50 hover:border-primary/30 hover:bg-muted/70 transition-colors">
                          <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
                            <ImageIcon size={24} />
                            <span className="text-xs">Add Photo</span>
                          </div>
                        </div>
                      )}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setImageFile(f); }} />
                    </label>

                    {/* Fields */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1.5 block">Title</Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Item name" className="border-border" />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1.5 block">Price</Label>
                          <div className="flex items-center rounded-md border border-border overflow-hidden">
                            <span className="flex items-center px-3 text-sm text-muted-foreground bg-muted h-10">$</span>
                            <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="border-0 rounded-l-none h-10" placeholder="0.00" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1.5 block">Quantity</Label>
                          <div className="flex items-center rounded-md border border-border h-10 overflow-hidden">
                            <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 h-full text-muted-foreground hover:text-foreground transition-colors"><Minus size={14} /></button>
                            <span className="flex-1 text-center text-sm font-medium">{quantity}</span>
                            <button type="button" onClick={() => setQuantity(quantity + 1)} className="px-3 h-full text-muted-foreground hover:text-foreground transition-colors"><Plus size={14} /></button>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-muted-foreground mb-1.5 block">Description</Label>
                        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Size Medium, The one in red, etc." rows={3} className="border-border resize-none" />
                      </div>
                    </div>
                  </div>

                  {linkUrl && (
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1.5 block">Link</Label>
                      <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className="border-border" />
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button type="button" onClick={() => { const cat = prompt("Enter a category (optional):"); if (cat !== null) setCategory(cat); }} className="text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-full px-3 py-1.5 transition-colors">
                      + Category
                    </button>
                  </div>

                  {category && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-primary/10 text-primary rounded-full px-2.5 py-1 font-medium">{category}</span>
                      <button onClick={() => setCategory("")} className="text-muted-foreground hover:text-foreground"><X size={12} /></button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sticky save button — manual mode only */}
            {mode === "manual" && (
              <div className="sticky bottom-0 bg-background border-t border-border px-5 py-4">
                <Button
                  onClick={handleSave}
                  disabled={saving || !title.trim()}
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  size="lg"
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    t("wishlist.saveWish", { defaultValue: "Save Wish" })
                  )}
                </Button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddWishModal;
